import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteIcon from '@mui/icons-material/Delete';
import StoreIcon from '@mui/icons-material/Store';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { theme } from '../../../theme';
import moment from 'moment';

const StyledCard = styled(Card)(({ theme: muiTheme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.borderRadius.xl,
    border: `1px solid ${theme.colors.border.light}`,
    boxShadow: theme.boxShadow.sm,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'visible',
    backgroundColor: '#fff',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 24px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -4px rgba(0, 0, 0, 0.08)',
        borderColor: theme.colors.primary[200],
    },
}));

const CardHeader = styled(Box)(({ theme: muiTheme }) => ({
    padding: muiTheme.spacing(2.5),
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: muiTheme.spacing(2),
}));

const AvatarBox = styled(Box)(({ theme: muiTheme }) => ({
    position: 'relative',
}));

const StatusBadge = styled(Box)(({ active }) => ({
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: active ? theme.colors.success[500] : theme.colors.text.tertiary,
    border: '2px solid #fff',
}));

const ProfessionalCard = ({
    user,
    onEdit,
    onHistory,
    onReset,
    onViewSalons,
    onDelete
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action) => {
        handleMenuClose();
        action();
    };

    const formatName = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Sans Nom';
    const lastLogin = user.last_login ? moment(user.last_login).fromNow() : 'Jamais';

    return (
        <StyledCard>
            <CardHeader>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <AvatarBox>
                        <Avatar
                            sx={{
                                width: 56,
                                height: 56,
                                bgcolor: theme.colors.secondary[100],
                                color: theme.colors.secondary[700],
                                fontWeight: 800,
                                fontSize: '1.25rem',
                                border: `2px solid ${theme.colors.secondary[200]}`
                            }}
                        >
                            {user.first_name?.[0]}{user.last_name?.[0]}
                        </Avatar>
                        <StatusBadge active={user.is_active} />
                    </AvatarBox>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: theme.colors.text.primary, lineHeight: 1.2 }}>
                            {formatName(user)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.colors.text.secondary, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 14, color: theme.colors.text.tertiary }} />
                            {user.email}
                        </Typography>
                        {user.phone && (
                            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: theme.colors.text.tertiary }} />
                                {user.phone}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <IconButton size="small" onClick={handleMenuClick}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </CardHeader>

            <CardContent sx={{ pt: 0, pb: 2, px: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                        label={`${user.salons_count || 0} Salon${(user.salons_count || 0) > 1 ? 's' : ''}`}
                        size="small"
                        icon={<StoreIcon sx={{ fontSize: '1rem !important' }} />}
                        sx={{
                            bgcolor: theme.colors.primary[50],
                            color: theme.colors.primary[700],
                            fontWeight: 700,
                            '& .MuiChip-icon': { color: theme.colors.primary[700] }
                        }}
                    />
                    <Chip
                        label={`${user.employees_count || 0} Employé${(user.employees_count || 0) > 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                            bgcolor: theme.colors.background.tertiary,
                            color: theme.colors.text.secondary,
                            fontWeight: 600
                        }}
                    />
                </Box>

                <Box sx={{ mt: 'auto', pt: 2, borderTop: `1px solid ${theme.colors.border.light}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, fontWeight: 500 }}>
                        Dernière connexion : {lastLogin}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Éditer">
                            <IconButton size="small" onClick={() => onEdit(user)} sx={{ color: theme.colors.text.secondary, '&:hover': { color: theme.colors.primary[600], bgcolor: theme.colors.primary[50] } }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Salons">
                            <IconButton size="small" onClick={() => onViewSalons(user)} sx={{ color: theme.colors.text.secondary, '&:hover': { color: theme.colors.primary[600], bgcolor: theme.colors.primary[50] } }}>
                                <StoreIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Historique">
                            <IconButton size="small" onClick={() => onHistory(user)} sx={{ color: theme.colors.text.secondary, '&:hover': { color: theme.colors.primary[600], bgcolor: theme.colors.primary[50] } }}>
                                <HistoryIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Réinit. mot de passe">
                            <IconButton size="small" onClick={() => onReset(user)} sx={{ color: theme.colors.text.secondary, '&:hover': { color: theme.colors.primary[600], bgcolor: theme.colors.primary[50] } }}>
                                <LockResetIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {onDelete && (
                            <Tooltip title="Supprimer">
                                <IconButton size="small" onClick={() => onDelete(user)} sx={{ color: theme.colors.text.secondary, '&:hover': { color: theme.colors.error[600], bgcolor: theme.colors.error[50] } }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </CardContent>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: theme.boxShadow.lg,
                        borderRadius: theme.borderRadius.lg,
                        border: `1px solid ${theme.colors.border.light}`,
                        minWidth: 160
                    }
                }}
            >
                <MenuItem onClick={() => handleAction(() => onEdit(user))}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Modifier" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                </MenuItem>
                <MenuItem onClick={() => handleAction(() => onViewSalons(user))}>
                    <ListItemIcon>
                        <StoreIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Voir les salons" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                </MenuItem>
                <MenuItem onClick={() => handleAction(() => onHistory(user))}>
                    <ListItemIcon>
                        <HistoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Historique" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                </MenuItem>
                <MenuItem onClick={() => handleAction(() => onReset(user))}>
                    <ListItemIcon>
                        <LockResetIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Réinit. mot de passe" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                </MenuItem>
                {onDelete && (
                    <MenuItem onClick={() => handleAction(() => onDelete(user))}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Supprimer" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                    </MenuItem>
                )}
            </Menu>
        </StyledCard>
    );
};

export default ProfessionalCard;
